import './CommunicationInput.scss'

import { AtIcon, Box, Grid, InputLabel } from '@forgeglobal/design-framework'
import { Document, SchemaProperties, Text, Value, ValueJSON } from 'slate'
import { Editor, EventHook, RenderInlineProps } from 'slate-react'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { TokenType } from 'graphql/bindings/AdminBindings'
import { ZIndex } from 'util/ZIndex'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import capitalize from 'lodash/capitalize'
import find from 'lodash/find'

import { ILabelValue, ITokenDefinition, tokenDefinitions } from './TokenDefinitions'
import { ISuggestionsHandles, Suggestions } from './Suggestions'

const initialValue = (notes: string) =>
  Value.fromJSON({
    document: ({
      nodes: [
        {
          object: 'block',
          type: 'paragraph',
          nodes: [{ object: 'text', leaves: [{ object: 'leaf', text: notes }] }],
        },
      ],
    } as unknown) as Document,
  })

type ITag = {
  type: string
  id: string
}

export type TaggedConversation = {
  text: string
  tokens: ITag[]
  json: ValueJSON
  accountId?: string
}

type CommunicationInputProps = {
  value: ValueJSON | undefined
  accountId?: string
  onChange?: (a: TaggedConversation) => void
  key?: string | number
  readOnly?: boolean
  notes?: string | null
  label: string
  isEditorTall?: boolean
}

const CommunicationInput: React.FC<CommunicationInputProps> = props => {
  const [currentTokenDefinition, setTokenDefinition] = useState<ITokenDefinition | null>(null)
  const [value, setValue] = useState(props.value ? Value.fromJSON(props.value) : initialValue(''))
  const [suggestions, setSuggestions] = useState<ILabelValue[]>([])

  const showSuggestions = useRef<boolean>(false)
  const suggestionsRef = useRef<ISuggestionsHandles>(null)
  const editorRef = useRef<Editor>(null)

  useEffect(() => {
    if (props.readOnly && props.value) {
      setValue(Value.fromJSON(props.value!))
    } else if (props.readOnly && props.notes) {
      setValue(initialValue(props.notes))
      editorRef.current!.insertText(props.notes)
    } else if (!props.readOnly && !props.value) {
      setValue(initialValue(''))
    }
  }, [props.readOnly, props.value, props.notes])

  const query: string | null = useMemo(() => {
    const trigger = currentTokenDefinition?.trigger
    if (!trigger || !value.focusText) return null

    const {
      focusText: { text },
      selection: {
        focus: { offset },
      },
    } = value

    const prevText = text.slice(0, offset)
    const start = prevText
      .split('')
      .reverse()
      .join('')
      .indexOf(trigger)

    if (start > -1) {
      return prevText.slice(offset - start)
    }
    return null
  }, [currentTokenDefinition, value])

  useEffect(() => {
    if (query !== null) {
      let queryFunction = currentTokenDefinition?.queryFunction!
      queryFunction(props.accountId)(query).then(setSuggestions)
    } else {
      hideSuggestions()
    }
  }, [query, currentTokenDefinition, props.accountId])

  const hideSuggestions = () => {
    setSuggestions([])
    showSuggestions.current = false
  }

  const onChange = ({ value }: { value: Value }) => {
    const tokens = value.document
      .getInlines()
      .map(i => ({ type: i!.data.get('type'), id: i!.data.get('id') }))
      .toJS()

    const apiType: TaggedConversation = {
      text: value.document.getText(),
      tokens,
      json: value.toJSON(),
    }

    if (props.onChange && !props.readOnly) {
      setValue(value)
      props.onChange(apiType)
    }
  }

  const handleBlur: EventHook<any> = (event, editor, next) => {
    if (showSuggestions.current) {
      event.preventDefault()
      hideSuggestions()
    } else {
      next()
    }
  }

  const handleSelect = (suggestion: ILabelValue) => {
    hideSuggestions()
    setTimeout(() => {
      editorRef
        .current!.deleteBackward(query!.length + 1)
        .insertInline({
          type: 'token',
          nodes: [Text.create(suggestion.label)],
          data: {
            name: suggestion.label,
            type: currentTokenDefinition!.name,
            id: suggestion.value,
          },
        })
        .moveForward()
    }, 0)
  }

  const onKeyDown: EventHook<any> = (e: any, editor, next) => {
    const event: KeyboardEvent = e
    if (showSuggestions.current && suggestionsRef.current) {
      switch (event.key) {
        case 'Enter':
          suggestionsRef.current.select()
          return event.preventDefault()
        case 'ArrowUp':
          suggestionsRef.current.up()
          return event.preventDefault()
        case 'ArrowDown':
          suggestionsRef.current.down()
          return event.preventDefault()
      }
    }

    handleTokenTrigger(event.key)

    next()
  }

  const handleTokenTrigger = (key: string) => {
    const tokenDefinition = tokenDefinitions
      .filter(tokenDef => (!props.accountId ? tokenDef.name !== TokenType.Opportunity : true))
      .find(td => td.trigger === key)

    if (tokenDefinition && !showSuggestions.current) {
      setTimeout(() => {
        setTokenDefinition(tokenDefinition)
        showSuggestions.current = true
      }, 0)
    }
  }

  const tokenMenu = (
    <Fragment>
      {tokenDefinitions.map(({ trigger, name }) => (
        <PopoverListItem
          key={name}
          button
          data-cypress={`${trigger}-trigger`}
          onClick={() => {
            editorRef.current!.insertText(trigger).focus()
            handleTokenTrigger(trigger)
          }}>
          <PopoverListItemIcon>
            <div style={{ width: 15, alignSelf: 'center' }}>{trigger}</div>
          </PopoverListItemIcon>
          <PopoverListItemText>Tag {capitalize(name)}</PopoverListItemText>
        </PopoverListItem>
      ))}
    </Fragment>
  )

  const renderInline = (props: RenderInlineProps, editor: any, next: any) => {
    switch (props.node.type) {
      case 'token':
        const tokenDefinition = find(tokenDefinitions, d => d.name === props.node.data.get('type'))
        if (tokenDefinition) {
          const Component = tokenDefinition!.tokenRenderer
          return <Component {...props} />
        }
        return next()
      default:
        return next()
    }
  }

  const fieldIsEmpty = value.document.getText() === ''

  return (
    <Box mt={props.readOnly ? undefined : 0} onClick={() => (editorRef.current as any).focus()}>
      <Grid key={props.key} style={{ position: 'relative' }}>
        {!props.readOnly && (
          <InputLabel
            style={{
              display: 'block',
              position: 'absolute',
              zIndex: props.isEditorTall ? ZIndex.CommunicationInputLabel : ZIndex.CommunicationInputLabelLower,
              marginTop: 16,
              marginLeft: 16,
            }}
            shrink={!fieldIsEmpty}>
            {props.label}
          </InputLabel>
        )}
        <Editor
          className={`editor ${props.readOnly ? '' : 'editor-editing'} ${
            props.isEditorTall ? 'editor-with-min-height' : ''
          }`}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={handleBlur}
          ref={editorRef}
          renderInline={renderInline}
          schema={schema}
          autoFocus={!props.readOnly}
          readOnly={props.readOnly}
          autoCorrect={false}
        />
        {!props.readOnly && (
          <div data-cypress="token-triggers-toggle" style={{ position: 'absolute', bottom: 5, right: 5 }}>
            <PopoverAction icon={<AtIcon />} actionMenu={tokenMenu} />
          </div>
        )}
        {showSuggestions.current && (
          <Suggestions
            title={currentTokenDefinition?.name ?? null}
            ref={suggestionsRef}
            suggestions={suggestions}
            onSelect={handleSelect}
            showPrompt={!currentTokenDefinition?.showWhenEmpty && query === ''}
          />
        )}
      </Grid>
    </Box>
  )
}

export default CommunicationInput

const schema: SchemaProperties = {
  document: {
    nodes: [
      {
        match: [{ type: 'paragraph' }],
      },
    ],
  },
  inlines: {
    token: {
      isVoid: true,
      data: {
        name: (v: any) => {
          return true
        },
      },
    },
  },
  blocks: {
    paragraph: {
      nodes: [
        {
          match: [{ object: 'text' }, { type: 'token' }],
        },
      ],
    },
  },
}
